// Typed API client + SSE-driven cache invalidation.

import { useEffect, useState } from "react";
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  AppEvent,
  ArtifactResponse,
  EngineAuthInfo,
  LibraryDoc,
  OrphanLaunch,
  RunDetail,
  RunSummary,
} from "@ideacrunch/shared";

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} for ${url}`);
  }
  return (await res.json()) as T;
}

export function useRuns(): UseQueryResult<RunSummary[]> {
  return useQuery({
    queryKey: ["runs"],
    queryFn: () => getJson<RunSummary[]>("/api/runs"),
  });
}

/** Launches that died before Intake wrote the run folder. */
export function useOrphanLaunches(): UseQueryResult<OrphanLaunch[]> {
  return useQuery({
    queryKey: ["launches"],
    queryFn: () => getJson<OrphanLaunch[]>("/api/launches"),
  });
}

/** Health of the Claude sign-in the engine runs on. Null data when the
 *  engine is disabled on this instance (the endpoint 503s). */
export function useEngineAuth(): UseQueryResult<EngineAuthInfo | null> {
  return useQuery({
    queryKey: ["engine-auth"],
    queryFn: async () => {
      const res = await fetch("/api/engine/auth");
      if (res.status === 503) return null;
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as EngineAuthInfo;
    },
    staleTime: 60_000,
  });
}

export function useRun(runId: string): UseQueryResult<RunDetail> {
  return useQuery({
    queryKey: ["run", runId],
    queryFn: () => getJson<RunDetail>(`/api/runs/${encodeURIComponent(runId)}`),
  });
}

export function useArtifact(
  runId: string,
  relPath: string,
): UseQueryResult<ArtifactResponse> {
  return useQuery({
    queryKey: ["artifact", runId, relPath],
    queryFn: () =>
      getJson<ArtifactResponse>(
        `/api/runs/${encodeURIComponent(runId)}/artifacts/${relPath}`,
      ),
    enabled: relPath.length > 0,
  });
}

export interface RunEvent {
  ts: number;
  type: string;
  text: string;
  lane: string | null;
  /** Present on "engine.stopped" rows when the machine asked explicitly. */
  questions?: string[];
}

export function useRunEvents(runId: string): UseQueryResult<RunEvent[]> {
  return useQuery({
    queryKey: ["run-events", runId],
    queryFn: () =>
      getJson<RunEvent[]>(
        `/api/runs/${encodeURIComponent(runId)}/events?limit=150`,
      ),
    staleTime: 30_000,
  });
}

export function useLibrary(): UseQueryResult<LibraryDoc[]> {
  return useQuery({
    queryKey: ["library"],
    queryFn: () => getJson<LibraryDoc[]>("/api/library"),
  });
}

// Live activity fan-out: components subscribe to raw AppEvents (activity
// drawer, engine pill) while TanStack Query handles data invalidation.
type AppEventListener = (event: AppEvent) => void;
const listeners = new Set<AppEventListener>();

export function subscribeAppEvents(listener: AppEventListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const VERSION_CHECK_MS = 5 * 60_000;

/**
 * Detect that the served bundle changed since this page loaded (deploys
 * rebuild the SPA without a page transition). Baseline is captured on boot;
 * re-checked on window focus and on an interval. Never auto-reloads - the
 * user may be mid-decision - it only reports, and the shell shows a toast.
 */
export function useUpdateAvailable(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let baseline: string | null = null;
    let stopped = false;

    const check = async () => {
      try {
        const res = await fetch("/api/version");
        if (!res.ok) return;
        const { web } = (await res.json()) as { web: string };
        if (stopped || web === "dev") return;
        if (baseline === null) baseline = web;
        else if (web !== baseline) setUpdateAvailable(true);
      } catch {
        // Offline or restarting; the next check covers it.
      }
    };

    void check();
    const interval = setInterval(() => void check(), VERSION_CHECK_MS);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      stopped = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return updateAvailable;
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: () =>
      getJson<{ ok: boolean; repoRoot: string; engineEnabled: boolean }>(
        "/api/health",
      ),
    staleTime: 60_000,
  });
}

/**
 * Subscribe to the server event stream and invalidate affected queries.
 * File-derived state is the commit point; every event is just a hint that a
 * refetch is worth it. On reconnect the wholesale refetch covers gaps.
 */
export function useEventStream(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const source = new EventSource("/api/events");

    const invalidateAll = () => {
      void queryClient.invalidateQueries({ queryKey: ["runs"] });
      void queryClient.invalidateQueries({ queryKey: ["library"] });
      // Also re-check interrupted launches: SSE reconnect after a server
      // restart is exactly when one may have appeared.
      void queryClient.invalidateQueries({ queryKey: ["launches"] });
    };

    const onEvent = (raw: MessageEvent) => {
      try {
        const event = JSON.parse(raw.data as string) as AppEvent;
        for (const l of listeners) l(event);
        if (event.type === "engine.activity" || event.type === "engine.stopped") {
          void queryClient.invalidateQueries({
            queryKey: ["run-events", event.runId],
          });
        }
        if (event.type === "run.updated") {
          void queryClient.invalidateQueries({ queryKey: ["runs"] });
          void queryClient.invalidateQueries({
            queryKey: ["run", event.runId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["artifact", event.runId],
          });
        } else if (event.type === "runs.changed") {
          invalidateAll();
        } else if (
          event.type === "gate.waiting" ||
          event.type === "gate.decided" ||
          event.type === "engine.status"
        ) {
          void queryClient.invalidateQueries({ queryKey: ["runs"] });
          void queryClient.invalidateQueries({
            queryKey: ["run", event.runId],
          });
        }
      } catch {
        // Malformed event: ignore; polling fallback covers us.
      }
    };

    for (const type of [
      "run.updated",
      "runs.changed",
      "gate.waiting",
      "gate.decided",
      "engine.status",
      "engine.activity",
      "engine.stopped",
      "sync.conflict",
    ]) {
      source.addEventListener(type, onEvent);
    }
    source.onopen = invalidateAll;

    return () => source.close();
  }, [queryClient]);
}
